import './stylesheets/main.scss'

import React, { useCallback, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { renderToString } from 'react-dom/server'
import { Provider } from 'react-redux'
import EditorKit, { EditorKitDelegate } from '@standardnotes/editor-kit'
import styled from 'styled-components'

import { store } from './app/store'
import { useAppDispatch, useAppSelector } from './app/hooks'
import CreateGroup from './features/tasks/CreateGroup'
import {
  setCanEdit,
  setIsRunningOnMobile,
  setSpellCheckerEnabled,
} from './features/settings/settings-slice'
import { tasksLoaded } from './features/tasks/tasks-slice'
import InvalidContentError from './features/tasks/InvalidContentError'
import MigrateLegacyContent from './features/tasks/MigrateLegacyContent'
import NotePreview from './features/tasks/NotePreview'
import TaskGroupList from './features/tasks/TaskGroupList'

import { getPlainPreview } from './common/utils'
import { CheckBoxElementsDefs } from './common/components/svg'
import { ErrorBoundaryFallback } from './common/components'
import { ErrorBoundary } from 'react-error-boundary'

const MainContainer = styled.div`
  margin: 16px;
`

const TaskEditor: React.FC = () => {
  const note = useRef<any>()
  const editorKit = useRef<EditorKit>()
  const hasReceivedFirstPayload = useRef<boolean>(false)
  /**
   * True while we're applying a note update that came from Standard Notes
   * (initial load or cross-client sync), as opposed to a local user edit.
   * The save-on-change subscription below checks this and skips, so we
   * don't write the just-received content straight back to the server —
   * which would cause an unnecessary round-trip and widen the race window
   * for sync conflicts. Reads/writes are all synchronous, so a ref is
   * sufficient (no need for state).
   */
  const isApplyingRemoteUpdate = useRef<boolean>(false)

  const initialized = useAppSelector((state) => state.tasks.initialized)
  const groupedTasks = useAppSelector((state) => state.tasks.groups)
  const legacyContent = useAppSelector((state) => state.tasks.legacyContent)

  const dispatch = useAppDispatch()

  function isRunningOnMobile(): boolean {
    return editorKit.current!.isRunningInMobileApplication()
  }

  const configureEditorKit = useCallback(() => {
    const editorKitDelegate: EditorKitDelegate = {
      setEditorRawText: (rawString: string) => {
        hasReceivedFirstPayload.current = true
        // editor-kit fires this on every cross-client sync, not just the
        // initial load. Suppress the save-on-change subscription for the
        // duration of the dispatch — Redux subscribers run synchronously
        // inside dispatch(), so this flag covers the relevant window.
        isApplyingRemoteUpdate.current = true
        try {
          dispatch(tasksLoaded(rawString))
        } finally {
          isApplyingRemoteUpdate.current = false
        }
      },
      onNoteValueChange: async (currentNote: any) => {
        note.current = currentNote

        const editable =
          !currentNote.content.appData['org.standardnotes.sn'].locked ?? true
        const spellCheckEnabled = currentNote.content.spellcheck

        dispatch(setCanEdit(editable))
        dispatch(setSpellCheckerEnabled(spellCheckEnabled))
        dispatch(setIsRunningOnMobile(isRunningOnMobile()))
      },
      onNoteLockToggle: (locked: boolean) => {
        dispatch(setCanEdit(!locked))
      },
    }

    editorKit.current = new EditorKit(editorKitDelegate, {
      mode: 'json',
      supportsFileSafe: false,
    })
  }, [dispatch])

  useEffect(() => {
    configureEditorKit()
  }, [configureEditorKit])

  /**
   * Safety fallback: if Standard Notes never delivers note data within 3s,
   * auto-initialize with empty state so the UI renders instead of hanging
   * on the loading screen forever.
   *
   * In normal operation, the bridge delivers the note in well under 3s
   * (typically <500ms) and `initialized` is already set by the time this
   * fires, making it a no-op. The fallback exists for the cases where the
   * bridge handshake silently fails — this was first observed on hardened
   * Android WebViews like GrapheneOS, where ComponentRelay used to throw on
   * a null parent origin and never sent the stream-context-item request.
   * That specific bug is fixed by patches/@standardnotes+editor-kit+2.2.5.patch,
   * but we keep the fallback as defence-in-depth for any future variant
   * (network hiccup, slow device, unknown WebView quirk) where the bridge
   * doesn't fire.
   *
   * IMPORTANT: hasReceivedFirstPayload.current stays false here. saveNote()
   * checks this ref and refuses to write back to the note when it's false,
   * which prevents the empty fallback state from overwriting real note data
   * if the bridge connects later than expected.
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const { initialized } = store.getState().tasks
      if (initialized === undefined) {
        const emptyState = JSON.stringify(
          { schemaVersion: '1.0.0', groups: [] },
          null,
          2
        )
        dispatch(tasksLoaded(emptyState))
        dispatch(setCanEdit(true))
      }
    }, 3000)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveNote = useCallback(() => {
    const { initialized } = store.getState().tasks
    const currentNote = note.current

    // Guard against writing the empty fallback state back to a note that
    // does have real content. hasReceivedFirstPayload is only set by
    // setEditorRawText (when SN delivers actual note data), so until that
    // fires we don't trust our in-memory state enough to save.
    if (!currentNote || !initialized || !hasReceivedFirstPayload.current) {
      return
    }

    const canEdit = store.getState().settings.canEdit
    if (!canEdit) {
      return
    }

    editorKit.current!.saveItemWithPresave(currentNote, () => {
      const { schemaVersion, groups } = store.getState().tasks
      currentNote.content.text = JSON.stringify(
        { schemaVersion, groups },
        null,
        2
      )

      currentNote.content.preview_plain = getPlainPreview(groups)
      currentNote.content.preview_html = renderToString(
        <NotePreview groupedTasks={groups} />
      )
    })
  }, [])

  /**
   * Save only when the tasks slice reference actually changes. Settings
   * dispatches (setCanEdit / setSpellCheckerEnabled / setIsRunningOnMobile)
   * fired from onNoteValueChange would otherwise trigger a save on every
   * note echo-back from Standard Notes — a feedback loop that floods the
   * mobile postMessage bridge and eventually surfaces editor-kit's 5s
   * "unable to communicate" alert, which blocks the iframe.
   */
  useEffect(() => {
    let prevTasks = store.getState().tasks
    const unsubscribe = store.subscribe(() => {
      const nextTasks = store.getState().tasks
      if (nextTasks === prevTasks) {
        return
      }
      prevTasks = nextTasks
      if (isApplyingRemoteUpdate.current) {
        return
      }
      saveNote()
    })
    return unsubscribe
  }, [saveNote])

  /**
   * Prevents dragging and dropping files
   */
  useEffect(() => {
    function rejectDragAndDrop(event: DragEvent) {
      event && event.preventDefault()
    }

    window.addEventListener('drop', rejectDragAndDrop)
    window.addEventListener('dragover', rejectDragAndDrop)

    return () => {
      window.removeEventListener('drop', rejectDragAndDrop)
      window.removeEventListener('dragover', rejectDragAndDrop)
    }
  }, [])

  if (legacyContent) {
    return (
      <MainContainer>
        <MigrateLegacyContent />
      </MainContainer>
    )
  }

  if (initialized === undefined) {
    // Still waiting for Standard Notes to deliver the note data (or for
    // the 3s fallback to fire). Show a neutral loading state instead of
    // the error UI, which would alarm the user during the brief window.
    return (
      <MainContainer>
        <div
          style={{
            color: 'var(--sn-stylekit-secondary-foreground-color)',
            padding: '40px 0',
            textAlign: 'center',
          }}
        >
          Loading…
        </div>
      </MainContainer>
    )
  }

  if (initialized === false) {
    // Initialization actually failed (e.g. parse error on note content).
    return (
      <MainContainer>
        <InvalidContentError />
      </MainContainer>
    )
  }

  if (groupedTasks.length === 0) {
    return (
      <MainContainer>
        <CreateGroup />
      </MainContainer>
    )
  }

  return (
    <>
      <CheckBoxElementsDefs />
      <MainContainer>
        <TaskGroupList />
        <CreateGroup />
      </MainContainer>
    </>
  )
}

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
        <TaskEditor />
      </ErrorBoundary>
    </Provider>
  </React.StrictMode>,
  document.getElementById('root')
)
